import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { C, G, IconProp, resolveIcon } from './uiTokens';

interface GlobalButtonProps {
  label: string;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'outline';
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  loading?: boolean;
  /** lucide component, element, or legacy "pi pi-xxx" string */
  icon?: IconProp;
  iconPos?: 'left' | 'right';
  fullWidth?: boolean;
  rounded?: boolean;
  style?: React.CSSProperties;
}

type VariantStyle = { bg: string; bgHover: string; color: string; border: string; shadow: string; shadowHover: string };

const VARIANTS: Record<NonNullable<GlobalButtonProps['variant']>, VariantStyle> = {
  primary:   { bg: 'rgb(17,85,80)',  bgHover: 'rgb(22,112,106)', color: 'white',  border: 'transparent',       shadow: '0 2px 8px rgba(17,85,80,0.25)',   shadowHover: '0 4px 14px rgba(17,85,80,0.4)' },
  success:   { bg: C.lime,           bgHover: '#b8d940',         color: C.teal,   border: 'transparent',       shadow: '0 2px 7px rgba(200,233,75,0.3)',  shadowHover: '0 4px 14px rgba(200,233,75,0.5)' },
  secondary: { bg: 'white',          bgHover: 'rgba(17,85,80,0.07)', color: C.sub, border: G.line,             shadow: 'none',                            shadowHover: 'none' },
  outline:   { bg: 'transparent',    bgHover: 'rgba(17,85,80,0.06)', color: C.teal, border: 'rgba(17,85,80,0.5)', shadow: 'none',                          shadowHover: 'none' },
  danger:    { bg: '#dc2626',        bgHover: '#b91c1c',         color: 'white',  border: 'transparent',       shadow: '0 2px 8px rgba(220,38,38,0.25)',  shadowHover: '0 4px 14px rgba(220,38,38,0.4)' },
  warning:   { bg: '#f59e0b',        bgHover: '#d97706',         color: 'white',  border: 'transparent',       shadow: '0 2px 8px rgba(245,158,11,0.25)', shadowHover: '0 4px 14px rgba(245,158,11,0.4)' },
};

const SIZES = {
  small:  { padding: '5.9px 12.9px',  fontSize: '0.73rem', icon: 13, gap: '4.9px' },
  medium: { padding: '8.9px 19.8px', fontSize: '0.8rem',  icon: 14, gap: '5.9px' },
  large:  { padding: '10.9px 24.7px', fontSize: '0.86rem', icon: 16, gap: '7px' },
};

export const GlobalButton: React.FC<GlobalButtonProps> = ({
  label,
  onClick,
  type = 'button',
  variant = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  icon,
  iconPos = 'left',
  fullWidth = false,
  rounded = true,
  style,
}) => {
  const [hovered, setHovered] = useState(false);
  const v = VARIANTS[variant];
  const s = SIZES[size];
  const inactive = disabled || loading;
  const active = hovered && !inactive;

  const iconNode = loading
    ? <Loader2 size={s.icon} className="global-button-spin" />
    : resolveIcon(icon, s.icon);

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={inactive}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="global-button"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: iconPos === 'right' ? 'row-reverse' : 'row',
        gap: s.gap,
        padding: s.padding,
        fontSize: s.fontSize,
        fontWeight: 700,
        letterSpacing: '0.2px',
        fontFamily: 'inherit',
        width: fullWidth ? '100%' : 'auto',
        background: active ? v.bgHover : v.bg,
        color: v.color,
        border: `1px solid ${v.border}`,
        borderRadius: rounded ? '8px' : '5.4px',
        boxShadow: inactive ? 'none' : active ? v.shadowHover : v.shadow,
        transform: active ? 'translateY(-1px)' : 'translateY(0)',
        opacity: inactive ? 0.6 : 1,
        cursor: inactive ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s ease',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {iconNode}
      {label}
      <style>{`
        .global-button-spin { animation: globalBtnSpin 0.9s linear infinite; }
        @keyframes globalBtnSpin { to { transform: rotate(360deg); } }
      `}</style>
    </button>
  );
};